import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { canImport, isAdmin, isStaff, ROLE_LABEL } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { useReviewCount } from '../pages/ReviewQueue.tsx';

/** dev.ngfgo.com runs the same build as production; this is how people tell them apart at a glance. */
export const IS_DEV = typeof location !== 'undefined' && location.hostname.startsWith('dev.');

export function DevBanner() {
  if (!IS_DEV) return null;
  return <div className="dev-banner" role="note">DEV environment · made-up test data only · never enter real patient information</div>;
}

export function Layout() {
  const { me, logout } = useAuth();
  if (!me) return null;
  return (
    <>
      <DevBanner />
      <header className={`topbar${IS_DEV ? ' topbar-dev' : ''}`}>
        <NavLink to="/"><img src="/ngf-logo.png" alt="NGF" /></NavLink>
        <nav>
          {isStaff(me.role) && <NavLink to="/" end>Dashboard</NavLink>}
          {isStaff(me.role) && <NavLink to="/events">Events</NavLink>}
          {isStaff(me.role) ? <NavLink to="/organizations">Organizations</NavLink> : <NavLink to="/portal">Portal</NavLink>}
          {isStaff(me.role) && <NavLink to="/participants">Participants</NavLink>}
          {isStaff(me.role) && <NavLink to="/assessments">Assessments</NavLink>}
          {(me.role === 'physician' || isAdmin(me.role)) && <NavLink to="/queue">Sign queue</NavLink>}
          {isStaff(me.role) && <NavLink to="/followups">Follow-ups</NavLink>}
          {isStaff(me.role) && <ReviewLink />}
          {canImport(me.role) && <NavLink to="/imports">Imports</NavLink>}
          {isStaff(me.role) && <NavLink to="/cover-sheets">Cover sheets</NavLink>}
          {isStaff(me.role) && <NavLink to="/find">Find records</NavLink>}
          {isStaff(me.role) && <NavLink to="/reports">Reports</NavLink>}
          {isStaff(me.role) && <NavLink to="/archive">Archive</NavLink>}
        </nav>
        {isStaff(me.role) && <Link className="btn field-btn" to="/field" title="iPad capture at events, works without a connection">Field mode</Link>}
        <Link to="/help" className="help-link top-help" title="How to use the app">? Help</Link>
        <UserMenu name={`${me.displayName}${me.credential ? `, ${me.credential}` : ''}`} role={ROLE_LABEL[me.role]} admin={isAdmin(me.role)} onSignOut={() => void logout()} />
      </header>
      <main><Outlet /></main>
    </>
  );
}

/** Your name, top right: a menu with the admin pages (admins), Help and Sign out (Danny, 2026-10-03). */
function UserMenu({ name, role, admin, onSignOut }: { name: string; role: string; admin: boolean; onSignOut: () => void }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [open]);
  const inAdmin = pathname.startsWith('/admin/');
  return (
    <div className="user-menu" ref={box}>
      <button type="button" className={`user-menu-btn${inAdmin ? ' active' : ''}`} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span><strong>{name}</strong><span className="muted">{role}</span></span><span aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="user-menu-list" role="menu">
          {admin && <>
            <div className="user-menu-head">Admin</div>
            <NavLink role="menuitem" to="/admin/users">Users</NavLink>
            <NavLink role="menuitem" to="/admin/physicians">Physicians</NavLink>
            <NavLink role="menuitem" to="/admin/audit">Audit log</NavLink>
            <NavLink role="menuitem" to="/admin/document-titles">Document titles</NavLink>
            <NavLink role="menuitem" to="/admin/duplicates">Possible duplicates</NavLink>
            <NavLink role="menuitem" to="/admin/scan-inboxes">Scanner inboxes</NavLink>
            <div className="user-menu-sep" />
          </>}
          <NavLink role="menuitem" to="/help">Help</NavLink>
          <button type="button" role="menuitem" onClick={onSignOut}>Sign out</button>
        </div>)}
    </div>
  );
}

/** "Review" with a count of walk-ins and duplicates waiting (refreshed when you navigate). */
function ReviewLink() {
  const n = useReviewCount();
  return <NavLink to="/review">Review{n > 0 && <span className="nav-count">{n}</span>}</NavLink>;
}

export function ErrorBox({ error }: { error: unknown }) {
  if (!error) return null;
  return <div className="alert error">{error instanceof Error ? error.message : String(error)}</div>;
}
